import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useLayout } from '../context/LayoutContext';
import api from '../services/api';

// ── Variablen auflösen ───────────────────────
const STATIC_VARS = {
  'recipes.count': '…',
  'date.year': new Date().getFullYear().toString(),
  'site.name': 'Strixly',
};

function resolve(val, content) {
  if (!val || typeof val !== 'string') return val;
  return val
    .replace(/\{\{content\.(\w+)\}\}/g, (_, key) => content?.[key] ?? '')
    .replace(/\{\{(\w+\.\w+)\}\}/g, (_, key) => STATIC_VARS[key] ?? '');
}

function resolveConfig(cfg, content) {
  if (!cfg) return cfg;
  const out = {};
  for (const [k, v] of Object.entries(cfg)) {
    out[k] = resolve(v, content);
  }
  return out;
}

// ── Hilfsfunktionen ──────────────────────────
function ha(hex, a) {
  if (!hex || hex.length < 7) return `rgba(0,0,0,${a})`;
  return `rgba(${parseInt(hex.slice(1,3),16)},${parseInt(hex.slice(3,5),16)},${parseInt(hex.slice(5,7),16)},${a})`;
}
function light(hex) {
  if (!hex || hex.length < 7) return true;
  const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16);
  return (r*299+g*587+b*114)/1000 > 128;
}

// ── Block-Komponenten ────────────────────────
function BlockText({ cfg, theme }) {
  const bodyIsHtml = (cfg.body || cfg.text || '').trimStart().startsWith('<');
  return (
    <div style={{ padding: `${cfg.paddingY||20}px 24px`, background: cfg.bgColor||'transparent', color: cfg.textColor||theme.text }}>
      {cfg.title && (
        <h2 style={{ color: theme.primary, fontFamily: theme.fontDisplay, textAlign: cfg.align||'left', marginBottom: '0.4em' }}>
          {cfg.title}
        </h2>
      )}
      {bodyIsHtml
        ? <div style={{ fontFamily: theme.fontBody, textAlign: cfg.align||'left' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cfg.body || cfg.text) }} />
        : <p style={{ fontFamily: theme.fontBody, textAlign: cfg.align||'left' }}>{cfg.body || cfg.text || ''}</p>
      }
    </div>
  );
}

function BlockBanner({ cfg, theme }) {
  return (
    <div style={{ padding: `${cfg.paddingY||12}px 24px`, background: theme.primary, color: '#fff', textAlign: 'center', fontWeight: 600 }}>
      {cfg.text || 'Ankündigung'}
      {cfg.link && <a href={cfg.link} style={{ color: theme.accent, marginLeft: 8 }}>{cfg.linkText || 'Mehr →'}</a>}
    </div>
  );
}

function BlockCta({ cfg, theme }) {
  return (
    <div style={{ padding: `${cfg.paddingY||26}px 24px`, textAlign: 'center', background: cfg.bgColor||'transparent' }}>
      <h3 style={{ color: theme.primary, fontFamily: theme.fontDisplay, marginBottom: 7, fontSize: '1.1rem' }}>{cfg.title || 'Bereit?'}</h3>
      <p style={{ fontFamily: theme.fontBody, opacity: 0.65, marginBottom: 14, fontSize: '.88rem' }}>{cfg.body || ''}</p>
      <button style={{ padding: '9px 26px', border: 'none', borderRadius: 50, fontSize: '.82rem', fontWeight: 600, cursor: 'pointer', background: theme.accent, color: light(theme.accent) ? '#111' : '#fff' }}>
        {cfg.btnText || 'Los'}
      </button>
    </div>
  );
}

function BlockStats({ cfg, theme }) {
  const items = [
    { n: cfg.s1n||'—', l: cfg.s1l||'Stat 1' },
    { n: cfg.s2n||'—', l: cfg.s2l||'Stat 2' },
    { n: cfg.s3n||'—', l: cfg.s3l||'Stat 3' },
  ];
  return (
    <div style={{ padding: `${cfg.paddingY||18}px 24px`, display: 'flex', justifyContent: 'space-around', textAlign: 'center', background: cfg.bgColor||'transparent' }}>
      {items.map((it, i) => (
        <div key={i}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.primary }}>{it.n}</div>
          <div style={{ fontSize: '.7rem', opacity: 0.55 }}>{it.l}</div>
        </div>
      ))}
    </div>
  );
}

function BlockAlert({ cfg, theme }) {
  const colors = { info: theme.accent, success: '#3fb950', warning: '#F4B942', error: '#f85149' };
  const icons  = { info: 'ℹ', success: '✓', warning: '⚠', error: '✕' };
  const t = cfg.type || 'info';
  const c = colors[t] || theme.accent;
  return (
    <div style={{ padding: `${cfg.paddingY||11}px 24px`, display: 'flex', alignItems: 'center', gap: 9, background: ha(c, 0.08), borderLeft: `4px solid ${c}` }}>
      <span>{icons[t]}</span>
      <span style={{ fontFamily: theme.fontBody, fontSize: '.85rem' }}>{cfg.text || 'Hinweis'}</span>
    </div>
  );
}

function BlockDivider({ cfg }) {
  return <div style={{ padding: `${cfg.paddingY||6}px 24px` }}><hr style={{ border: 'none', borderTop: '1.5px solid rgba(0,0,0,.1)' }} /></div>;
}

function BlockSpacer({ cfg }) {
  return <div style={{ height: `${cfg.height || 36}px` }} />;
}

function BlockImage({ cfg }) {
  return (
    <div style={{ padding: '14px 24px', textAlign: 'center' }}>
      {cfg.imgSrc
        ? <img src={cfg.imgSrc} alt={cfg.caption || ''} style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 7, objectFit: 'cover', display: 'block', margin: '0 auto' }} />
        : <div style={{ background: 'linear-gradient(135deg,#e8e8e0,#f5f5f0)', height: 160, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', color: '#ccc' }}>🖼</div>
      }
      {cfg.caption && <p style={{ fontSize: '.72rem', color: '#888', marginTop: 5 }}>{cfg.caption}</p>}
    </div>
  );
}

function BlockNav({ cfg, theme, globalCfg }) {
  const g = globalCfg || {};
  const showUpload = cfg.showUpload !== false && cfg.showUpload !== 'false';
  return (
    <nav style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px', background: theme.primary, minHeight: g.navHeight || 64, boxShadow: g.navShadow ? '0 2px 8px rgba(0,0,0,.18)' : 'none' }}>
      <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
        {g.imgLogo
          ? <img src={g.imgLogo} alt="Logo" style={{ height: 32, objectFit: 'contain' }} />
          : <span style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#fff' }}>{(g.siteName||'R')[0]}</span>
        }
        <span style={{ fontWeight: 700, fontSize: '.95rem', color: '#fff', fontFamily: theme.fontDisplay }}>{cfg.logoText || g.siteName || 'Strixly'}</span>
      </Link>
      <div style={{ display: 'flex', gap: 20 }}>
        <Link to="/rezepte" style={{ color: 'rgba(255,255,255,.85)', textDecoration: 'none', fontSize: '.82rem' }}>Rezepte</Link>
        {showUpload && <Link to="/new" style={{ color: 'rgba(255,255,255,.85)', textDecoration: 'none', fontSize: '.82rem' }}>Upload</Link>}
      </div>
    </nav>
  );
}

function BlockHero({ cfg, theme, globalCfg }) {
  const g = globalCfg || {};
  const bg = g.imgHeroBg
    ? `linear-gradient(rgba(255,255,255,.1),rgba(255,255,255,.1)),url(${g.imgHeroBg}) center/cover`
    : `linear-gradient(135deg,${ha(theme.primary,.08)},${ha(theme.accent,.12)})`;
  return (
    <div style={{ padding: '44px 24px 32px', textAlign: 'center', background: bg }}>
      <h1 style={{ fontSize: 'clamp(1.5rem,3vw,2.6rem)', marginBottom: 10, color: theme.primary, fontFamily: theme.fontDisplay }}>
        {cfg.title || g.heroTitle || 'Entdecke unsere Rezeptsammlung'}
      </h1>
      <p style={{ fontSize: '.88rem', opacity: 0.65, maxWidth: 420, margin: '0 auto 18px', fontFamily: theme.fontBody }}>
        {cfg.sub || g.heroSub || 'Hausgemacht. Erprobt. Geliebt.'}
      </p>
      <button style={{ display: 'inline-block', padding: '9px 24px', borderRadius: 50, fontSize: '.82rem', fontWeight: 600, color: light(theme.accent)?'#111':'#fff', border: 'none', cursor: 'pointer', background: theme.accent }}>
        {cfg.btnText || g.heroBtnText || 'Alle Rezepte'}
      </button>
    </div>
  );
}

function BlockFooter({ cfg, theme, globalCfg }) {
  const g = globalCfg || {};
  return (
    <footer style={{ padding: '14px 24px', textAlign: 'center', fontSize: '.74rem', borderTop: '1px solid rgba(0,0,0,.07)', color: '#555', fontFamily: theme.fontBody }}>
      {cfg.text || g.footerText || `© ${new Date().getFullYear()} Strixly`}
    </footer>
  );
}

// ── Rezepte-Sektion (Search + Filters + Recipes) ─────────────────────
function RecipesSection({ searchCfg, filtersCfg, recipesCfg, theme }) {
  const [recipes, setRecipes]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch]       = useState('');
  const [category, setCategory]   = useState('');
  const [loading, setLoading]     = useState(true);

  const load = useCallback(async (q, cat) => {
    setLoading(true);
    try {
      const d = q
        ? await api.searchRecipes(q)
        : await api.getRecipes(cat ? { category: cat } : {});
      setRecipes(d.recipes || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    async function init() {
      try {
        const d = await api.getCategories();
        setCategories(d.categories || []);
      } catch { /* Kategorien optional */ }
      load('', '');
    }
    init();
  }, [load]);

  function handleSearch(e) {
    e.preventDefault();
    load(search, category);
  }

  const limit = parseInt(recipesCfg?.limit || '12', 10);
  const title = recipesCfg?.sectionTitle;

  return (
    <div style={{ width: '100%', padding: '0 24px 32px' }}>
      {/* Search */}
      {searchCfg !== null && (
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, margin: '16px 0' }}>
          <input
            type="text"
            placeholder={searchCfg?.placeholder || 'Rezept suchen…'}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, padding: '8px 14px', borderRadius: 50, border: '1.5px solid rgba(0,0,0,.12)', fontSize: '.85rem', fontFamily: theme.fontBody, outline: 'none' }}
          />
          <button type="submit" style={{ padding: '8px 20px', borderRadius: 50, border: 'none', background: theme.primary, color: '#fff', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer' }}>
            Suchen
          </button>
          {search && (
            <button type="button" onClick={() => { setSearch(''); load('', category); }}
              style={{ padding: '8px 14px', borderRadius: 50, border: '1.5px solid rgba(0,0,0,.12)', background: 'none', fontSize: '.82rem', cursor: 'pointer' }}>
              ✕
            </button>
          )}
        </form>
      )}
      {/* Filters */}
      {filtersCfg !== null && categories.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {['', ...categories].map(cat => (
            <button key={cat || '__all'} onClick={() => { setCategory(cat); load(search, cat); }}
              style={{ padding: '5px 14px', borderRadius: 50, border: `1.5px solid ${category === cat ? theme.primary : 'rgba(0,0,0,.12)'}`, background: category === cat ? theme.primary : 'transparent', color: category === cat ? '#fff' : theme.text, fontSize: '.78rem', cursor: 'pointer', fontFamily: theme.fontBody }}>
              {cat || 'Alle'}
            </button>
          ))}
        </div>
      )}
      {/* Recipes Grid */}
      {title && <h2 style={{ fontFamily: theme.fontDisplay, color: theme.primary, marginBottom: 16 }}>{title}</h2>}
      {loading
        ? <div style={{ textAlign: 'center', padding: 32, color: '#aaa' }}>Lädt…</div>
        : recipes.length === 0
          ? <div style={{ textAlign: 'center', padding: 32, color: '#aaa' }}>Keine Rezepte gefunden.</div>
          : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 16 }}>
              {recipes.slice(0, limit).map(rec => (
                <Link key={rec.id} to={`/recipe/${rec.id}`} style={{ textDecoration: 'none', background: theme.card, borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,.07)', display: 'block' }}>
                  {rec.image_url
                    ? <img src={rec.image_url} alt={rec.title} style={{ width: '100%', height: 130, objectFit: 'cover' }} />
                    : <div style={{ height: 130, background: `linear-gradient(135deg,${theme.primary}22,${theme.accent}22)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem' }}>🍽</div>
                  }
                  <div style={{ padding: '10px 12px' }}>
                    <div style={{ fontWeight: 600, fontSize: '.85rem', color: theme.text, fontFamily: theme.fontDisplay, marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rec.title}</div>
                    {rec.category && <div style={{ fontSize: '.7rem', color: theme.primary, fontFamily: theme.fontBody }}>{rec.category}</div>}
                  </div>
                </Link>
              ))}
            </div>
          )
      }
    </div>
  );
}

// ── Block-Dispatcher ─────────────────────────
function Block({ block, theme, globalCfg, content, isEmbedded }) {
  const cfg = resolveConfig(block.config || {}, content);
  const wMap = { '25':'25%','33':'33.3333%','50':'50%','66':'66.6667%','75':'75%','100':'100%' };
  const style = {
    width: wMap[block.w] || '100%',
    boxSizing: 'border-box',
    alignSelf: block.valign === 'center' ? 'center' : block.valign === 'stretch' ? 'stretch' : 'flex-start',
  };
  // In eingebettetem Modus (innerhalb React Layout): nav/footer vom CMS weglassen
  if (isEmbedded && (block.type === 'nav' || block.type === 'footer')) return null;

  let blockContent;
  switch (block.type) {
    case 'nav':     blockContent = <BlockNav     cfg={cfg} theme={theme} globalCfg={globalCfg} />; break;
    case 'hero':    blockContent = <BlockHero    cfg={cfg} theme={theme} globalCfg={globalCfg} />; break;
    case 'footer':  blockContent = <BlockFooter  cfg={cfg} theme={theme} globalCfg={globalCfg} />; break;
    case 'text':    blockContent = <BlockText    cfg={cfg} theme={theme} />; break;
    case 'banner':  blockContent = <BlockBanner  cfg={cfg} theme={theme} />; break;
    case 'cta':     blockContent = <BlockCta     cfg={cfg} theme={theme} />; break;
    case 'stats':   blockContent = <BlockStats   cfg={cfg} theme={theme} />; break;
    case 'alert':   blockContent = <BlockAlert   cfg={cfg} theme={theme} />; break;
    case 'divider': blockContent = <BlockDivider cfg={cfg} />; break;
    case 'spacer':  blockContent = <BlockSpacer  cfg={cfg} />; break;
    case 'image':   blockContent = <BlockImage   cfg={cfg} />; break;
    // search/filters/recipes werden als RecipesSection gebündelt (see below)
    case 'search':
    case 'filters':
    case 'recipes':
      return null; // handled by PageRenderer directly
    default:        blockContent = null;
  }
  return blockContent ? <div style={style}>{blockContent}</div> : null;
}

// ── Haupt-Komponente ─────────────────────────
// Wenn embedded=true (innerhalb React Layout): nav/footer CMS-Blöcke überspringen
export default function PageRenderer({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const isEmbedded = !!slugProp; // innerhalb Layout.js → eigene nav/footer weglassen
  const { globalCfg } = useLayout();
  const [layout, setLayout]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.getPublicLayout(slug)
      .then(d => {
        if (d.fallback || !d.layout) { setNotFound(true); return; }
        setLayout(d.layout.layout_json || {});
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Lade Seite…</div>;
  if (notFound) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: '3rem', marginBottom: 16 }}>📄</div>
      <h2 style={{ marginBottom: 8 }}>Seite nicht gefunden</h2>
      <p style={{ color: '#888', marginBottom: 24 }}>Die Seite „{slug}" existiert nicht.</p>
      <Link to="/" style={{ color: '#1f6feb' }}>← Zurück zur Startseite</Link>
    </div>
  );

  // Theme aus gespeichertem Layout + globalen Defaults zusammenführen
  const g = globalCfg || {};
  const theme = {
    primary:     layout.primary     || g.primary     || '#5D7361',
    accent:      layout.accent      || g.accent      || '#F4B942',
    bg:          layout.bg          || g.bg          || '#F9F7F2',
    text:        layout.text        || g.text        || '#2C332D',
    card:        layout.card        || g.card        || '#ffffff',
    fontDisplay: layout.fontDisplay || g.fontDisplay || "'Playfair Display',serif",
    fontBody:    layout.fontBody    || g.fontBody    || "'Inter',sans-serif",
    navHeight:   layout.navHeight   || g.navHeight   || 64,
    navShadow:   layout.navShadow   ?? g.navShadow   ?? true,
  };

  const blocks  = layout.blocks || [];
  const content = layout.content || {};

  // Fallback wenn keine Blöcke gespeichert
  if (blocks.length === 0) {
    return (
      <div style={{ background: theme.bg, minHeight: isEmbedded ? 300 : '100vh', fontFamily: theme.fontBody }}>
        {!isEmbedded && <BlockNav cfg={{}} theme={theme} globalCfg={layout} />}
        <div style={{ padding: '60px 24px', textAlign: 'center', color: '#888' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>📄</div>
          <p>Diese Seite hat noch keine Blöcke.</p>
          <a href={`/editor?scope=${slug}`} style={{ color: '#1f6feb' }}>Im Layout-Editor bearbeiten →</a>
        </div>
        {!isEmbedded && <BlockFooter cfg={{}} theme={theme} globalCfg={layout} />}
      </div>
    );
  }

  // Rezepte-relevante Blöcke einsammeln für gemeinsamen State
  const searchBlock  = blocks.find(b => b.type === 'search');
  const filtersBlock = blocks.find(b => b.type === 'filters');
  const recipesBlock = blocks.find(b => b.type === 'recipes');
  const hasRecipeSection = searchBlock || filtersBlock || recipesBlock;

  // Position der ersten Rezepte-Block-Gruppe bestimmen (zum Einsetzen)
  const firstRecipeIdx = blocks.findIndex(b => ['search','filters','recipes'].includes(b.type));

  return (
    <div style={{ background: theme.bg, minHeight: '100vh', fontFamily: theme.fontBody, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {blocks.map((block, i) => {
        const type = block.type;
        // Rezepte-Blöcke: nur beim ersten vorkommen die gebündelte Sektion rendern
        if (['search','filters','recipes'].includes(type)) {
          if (i === firstRecipeIdx && hasRecipeSection) {
            return (
              <RecipesSection
                key="recipes-section"
                searchCfg={searchBlock ? (searchBlock.config || {}) : null}
                filtersCfg={filtersBlock ? (filtersBlock.config || {}) : null}
                recipesCfg={recipesBlock ? (recipesBlock.config || {}) : null}
                theme={theme}
              />
            );
          }
          return null;
        }
        return <Block key={block.id || i} block={block} theme={theme} globalCfg={layout} content={content} isEmbedded={isEmbedded} />;
      })}
    </div>
  );
}
